export const idlFactory = ({ IDL }) => {
  const MintRequest = IDL.Record({
    'recipient' : IDL.Opt(IDL.Principal),
    'quantity' : IDL.Nat,
  });
  const MintResponse = IDL.Record({
    'success' : IDL.Bool,
    'token_ids' : IDL.Vec(IDL.Nat),
  });
  const Balance = IDL.Nat;
  const TokenIndex = IDL.Nat;
  const TokenMetadata = IDL.Record({ 'metadata' : IDL.Opt(IDL.Vec(IDL.Nat8)) });
  const AccountIdentifier = IDL.Vec(IDL.Nat8);
  const Memo = IDL.Vec(IDL.Nat8);
  const SubAccount = IDL.Vec(IDL.Nat8);
  const TransferRequest = IDL.Record({
    'to' : AccountIdentifier,
    'token' : TokenIndex,
    'notify' : IDL.Bool,
    'from' : AccountIdentifier,
    'memo' : Memo,
    'subaccount' : IDL.Opt(SubAccount),
  });
  const TransferError = IDL.Variant({
    'CannotNotify' : AccountIdentifier,
    'InsufficientBalance' : IDL.Record({ 'balance' : Balance }),
    'InvalidToken' : TokenIndex,
    'Rejected' : IDL.Record({ 'fee' : Balance }),
    'Unauthorized' : AccountIdentifier,
  });
  const TransferResponse = IDL.Variant({
    'Ok' : Balance,
    'Err' : TransferError,
  });
  const GeneratedNFT = IDL.Record({
    'token_id' : IDL.Nat,
    'metadata' : IDL.Text,
    'rarity_score' : IDL.Float64,
    'composite_image' : IDL.Opt(IDL.Vec(IDL.Nat8)),
    'layers' : IDL.Vec(IDL.Text),
  });
  const AdminConfig = IDL.Record({
    'icrc7_installed' : IDL.Bool,
    'ext_installed' : IDL.Bool,
    'icrc37_installed' : IDL.Bool,
    'target_canister_id' : IDL.Principal,
  });
  const FarmStats = IDL.Record({
    'members' : IDL.Nat64,
    'pepper_plants' : IDL.Nat64,
    'last_updated' : IDL.Nat64,
    'co2_offset' : IDL.Text,
    'harvest_yield' : IDL.Text,
  });
  const UserRole = IDL.Variant({ 'admin' : IDL.Null, 'user' : IDL.Null });
  const TransactionType = IDL.Variant({
    'Mint' : IDL.Null,
    'Transfer' : IDL.Null,
    'Claim' : IDL.Null,
  });
  const NFTTransaction = IDL.Record({
    'id' : IDL.Nat64,
    'to' : IDL.Principal,
    'transaction_type' : TransactionType,
    'token_id' : IDL.Nat64,
    'from' : IDL.Opt(IDL.Principal),
    'memo' : IDL.Opt(IDL.Text),
    'timestamp' : IDL.Nat64,
  });
  const ICRC37TransferRequest = IDL.Record({
    'to' : IDL.Principal,
    'token_id' : IDL.Nat,
    'from' : IDL.Principal,
  });
  const ICRC7Token = IDL.Record({
    'id' : IDL.Nat,
    'owner' : IDL.Principal,
    'metadata' : IDL.Text,
  });
  const LayerUpload = IDL.Record({
    'layer_name' : IDL.Text,
    'trait_images' : IDL.Vec(
      IDL.Record({ 'data' : IDL.Vec(IDL.Nat8), 'name' : IDL.Text })
    ),
  });
  const ProjectUpload = IDL.Record({
    'layers' : IDL.Vec(LayerUpload),
    'project_name' : IDL.Text,
  });
  return IDL.Service({
    'batch_mint' : IDL.Func([MintRequest], [MintResponse], []),
    'check_delegation_expiry' : IDL.Func([], [IDL.Nat64], []),
    'collect_platform_fee' : IDL.Func(
        [IDL.Principal, IDL.Nat64],
        [IDL.Text],
        [],
      ),
    'create_collection' : IDL.Func([IDL.Text, IDL.Text], [IDL.Principal], []),
    'deploy_bitcoin_ordinal' : IDL.Func([IDL.Text], [IDL.Text], []),
    'deploy_evm_contract' : IDL.Func([IDL.Text], [IDL.Text], []),
    'deploy_solana_program' : IDL.Func([IDL.Text], [IDL.Text], []),
    'ext_balance' : IDL.Func([IDL.Principal], [Balance], []),
    'ext_getTokenMetadata' : IDL.Func([TokenIndex], [TokenMetadata], []),
    'ext_getTokens' : IDL.Func([IDL.Principal], [IDL.Vec(TokenIndex)], []),
    'ext_mint' : IDL.Func(
        [IDL.Principal, IDL.Opt(IDL.Vec(IDL.Nat8))],
        [IDL.Variant({ 'Ok' : TokenIndex, 'Err' : IDL.Text })],
        [],
      ),
    'ext_ownerOf' : IDL.Func([TokenIndex], [IDL.Opt(IDL.Principal)], []),
    'ext_tokens' : IDL.Func([IDL.Nat, IDL.Nat], [IDL.Vec(TokenIndex)], []),
    'ext_transfer' : IDL.Func([TransferRequest], [TransferResponse], []),
    'generate_nft' : IDL.Func([], [GeneratedNFT], []),
    'get_admin_config' : IDL.Func([], [AdminConfig], []),
    'get_admin_principal' : IDL.Func([], [IDL.Opt(IDL.Principal)], []),
    'get_all_nfts' : IDL.Func([], [IDL.Vec(GeneratedNFT)], []),
    'get_analytics' : IDL.Func([], [IDL.Text], []),
    'get_collection_canister_id' : IDL.Func(
        [IDL.Nat],
        [IDL.Opt(IDL.Principal)],
        [],
      ),
    'get_farm_stats' : IDL.Func([], [FarmStats], ['query']),
    'get_layers' : IDL.Func([], [IDL.Vec(IDL.Text)], []),
    'get_nft_metadata' : IDL.Func([IDL.Nat], [IDL.Opt(IDL.Text)], []),
    'get_oisy_balance' : IDL.Func(
        [IDL.Principal],
        [IDL.Variant({ 'Ok' : IDL.Nat64, 'Err' : IDL.Text })],
        [],
      ),
    'get_rarity_score' : IDL.Func([IDL.Nat], [IDL.Float64], []),
    'get_user_collections' : IDL.Func(
        [IDL.Principal],
        [IDL.Variant({ 'Ok' : IDL.Vec(IDL.Text), 'Err' : IDL.Text })],
        [],
      ),
    'get_user_role_query' : IDL.Func([IDL.Principal], [IDL.Opt(UserRole)], []),
    'get_user_tokens' : IDL.Func([IDL.Principal], [IDL.Vec(IDL.Nat)], []),
    'get_user_transactions' : IDL.Func(
        [IDL.Principal],
        [IDL.Vec(NFTTransaction)],
        ['query'],
      ),
    'icrc37_transfer' : IDL.Func([ICRC37TransferRequest], [IDL.Bool], []),
    'icrc7_getToken' : IDL.Func([IDL.Nat], [IDL.Opt(ICRC7Token)], []),
    'icrc7_getTokens' : IDL.Func([IDL.Principal], [IDL.Vec(ICRC7Token)], []),
    'install_standards' : IDL.Func([], [IDL.Text], []),
    'mint' : IDL.Func([IDL.Opt(IDL.Principal)], [MintResponse], []),
    'renew_delegation' : IDL.Func([], [IDL.Text], []),
    'set_oisy_wallet' : IDL.Func([IDL.Principal], [IDL.Text], []),
    'set_target_canister' : IDL.Func([IDL.Principal], [IDL.Text], []),
    'transfer_nft' : IDL.Func([IDL.Principal, IDL.Nat], [IDL.Bool], []),
    'update_farm_stats' : IDL.Func(
        [
          IDL.Opt(IDL.Nat64),
          IDL.Opt(IDL.Nat64),
          IDL.Opt(IDL.Text),
          IDL.Opt(IDL.Text),
        ],
        [IDL.Variant({ 'Ok' : FarmStats, 'Err' : IDL.Text })],
        [],
      ),
    'upload_project' : IDL.Func([ProjectUpload], [IDL.Text], []),
  });
};
export const init = ({ IDL }) => { return []; };
